import { useRef, useState } from 'react';
import { Button } from '@/components/ui/therapist/Button';
import { View, Modal as RNModal, Dimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { NeolexonIcon } from '@/components/NeolexonIcon';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { Template } from '@/components/Template/types';
import { Text } from '@/components/ui/therapist/Text';
import { useStoreState } from '@/stores/store';
import { useOnHover } from '@/hooks/useOnHover';

import colors from '@/config/colors';
import { styles } from './styles';
import { Divider } from 'react-native-elements';

type TemplateMenuProps = {
  template: Template;
  handleTemplateDelete: () => void;
};

const SCREEN_NAME_ACCORD_TYPE = {
  Word: 'WordSet',
  Sentence: 'SentenceSet',
  Text: 'TextSet',
} as const;

const API_NAME_ACCORD_TYPE = {
  Word: 'wordsets',
  Sentence: 'sentencesets',
  Text: 'textsets',
};

export const TemplateMenu: React.FC<TemplateMenuProps> = ({
  template,
  handleTemplateDelete,
}) => {
  const { isTherapistDemo } = useStoreState((store) => store);
  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const [menuPosition, setMenuPosition] = useState<{
    bottom: number;
    right: number;
  }>({ bottom: 0, right: 0 });

  const [openMenuTemplateId, setOpenMenuTemplateId] = useState<string | null>(
    null,
  );
  const { handleHoverIn, handleHoverOut } = useOnHover();
  const ellipsisRef = useRef<View>(null);

  return (
    <>
      <Button
        label="Vorlage nutzen"
        look="secondary"
        onPress={() => navigate('TemplateAssign', { template })}
        style={{ flex: 1, justifyContent: 'center' }}
      />
      <PressableNeolexon
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        ref={ellipsisRef}
        style={styles.ellipsisBtn}
        onPress={() => {
          ellipsisRef.current?.measureInWindow((x, y, width, height) => {
            setMenuPosition({
              right: Dimensions.get('window').width - (x + width),
              bottom: Dimensions.get('window').height - (y + height),
            });
            setOpenMenuTemplateId(template.id);
          });
        }}
      >
        <NeolexonIcon name="ellipsis" size={40} />
      </PressableNeolexon>
      <RNModal transparent visible={openMenuTemplateId === template.id}>
        <PressableNeolexon
          style={{ flex: 1 }}
          onPress={() => setOpenMenuTemplateId(null)}
        />
        <View
          style={[
            styles.menuWrapper,
            {
              right: menuPosition.right,
              bottom: menuPosition.bottom,
            },
          ]}
        >
          <View style={styles.menuButtonWrapper}>
            <PressableNeolexon
              disabled={template.isNeolexonTemplate || isTherapistDemo}
              onPress={() => {
                setOpenMenuTemplateId(null);

                navigate(SCREEN_NAME_ACCORD_TYPE[template.setType], {
                  apiName: API_NAME_ACCORD_TYPE[template.setType],
                  templateMode: 'update',
                  fromTemplate: template.id,
                });
              }}
              style={[
                {
                  padding: 20,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                },
                (template.isNeolexonTemplate || isTherapistDemo) && {
                  backgroundColor: colors.newUI.therapist.grey20,
                  opacity: 0.5,
                },
              ]}
            >
              <Text style={{ textAlign: 'right' }}>Bearbeiten</Text>
            </PressableNeolexon>
            <Divider color={colors.newUI.therapist.blue20} />

            <PressableNeolexon
              disabled={isTherapistDemo}
              onPress={() => {
                navigate(SCREEN_NAME_ACCORD_TYPE[template.setType], {
                  apiName: API_NAME_ACCORD_TYPE[template.setType],
                  templateMode: 'create',
                  fromTemplate: template.id,
                });
                setOpenMenuTemplateId(null);
              }}
              style={[
                {
                  padding: 20,
                },
                isTherapistDemo && {
                  backgroundColor: colors.newUI.therapist.grey20,
                  opacity: 0.5,
                },
              ]}
            >
              <Text style={{ textAlign: 'right' }}>Duplizieren</Text>
            </PressableNeolexon>
            <Divider color={colors.newUI.therapist.blue20} />
            <PressableNeolexon
              disabled={template.isNeolexonTemplate}
              onPress={() => {
                setOpenMenuTemplateId(null);
                handleTemplateDelete();
              }}
              style={[
                {
                  padding: 20,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                },
                template.isNeolexonTemplate && {
                  backgroundColor: colors.newUI.therapist.grey20,
                  opacity: 0.5,
                },
              ]}
            >
              <Text style={{ textAlign: 'right', color: 'red' }}>Löschen</Text>
            </PressableNeolexon>
          </View>
        </View>
      </RNModal>
    </>
  );
};
